import React from "react";

import { useCurrentUser, useLoginMethod } from "@accurx/auth";
import { ButtonLink, Ds } from "@accurx/design";
import {
    useGenerateTwoFactorToken,
    useWorkspaceRequiresToBeMappedByUser,
} from "@accurx/workspace-management";
import { Redirect, generatePath, useLocation } from "react-router-dom";
import { useSelectedOrganisation } from "reduxQuarantine/SelectedOrganisationProvider";
import { useAnalytics } from "reduxQuarantine/useAnalytics";
import styled from "styled-components";

import { ROUTES_WORKSPACE } from "shared/Routes";

import { InnerStackPanel, OuterStackPanel } from "../LogIn/LoginStackPanel";

const StyledCenteredButtonLink = styled(ButtonLink)`
    width: 100%;
`;

const StyledContainer = styled(Ds.Flex).attrs({
    alignItems: "center",
    justifyContent: "center",
})`
    width: 100%;
    height: 100%;
`;

const StyledContent = styled(Ds.Flex).attrs({
    flexDirection: "column",
    alignItems: "center",
    gap: "1",
})`
    text-align: center;
`;

export const DesktopLaunch = (): JSX.Element => {
    const track = useAnalytics();
    const location = useLocation();

    const { user } = useCurrentUser();
    const loginMethod = useLoginMethod();
    const urlParams = new URLSearchParams(location.search);

    const { status, data } = useGenerateTwoFactorToken({
        email: user.email,
        usingUserId: urlParams.get("usingUserId") === "true" ? "true" : "false", // defaults to false if not available
        loginCorrelationId: urlParams.get("loginCorrelationId") ?? undefined,
    });

    const { selectedOrgId: workspaceId } = useSelectedOrganisation();

    const currentWorkspace = workspaceId
        ? user.organisations.find(({ orgId }) => orgId === workspaceId)
        : null;

    const isAdminUser = !!currentWorkspace?.settings.isAdminUser;

    const { data: isUserRequiredToMapWorkspace } =
        useWorkspaceRequiresToBeMappedByUser({
            workspaceId,
            enabled: !!workspaceId && isAdminUser,
        });

    if (workspaceId && isUserRequiredToMapWorkspace) {
        return (
            <Redirect
                to={{
                    ...location,
                    pathname: generatePath(
                        ROUTES_WORKSPACE.workspaceMapWorkspaceToOrganisation,
                        { workspaceId },
                    ),
                }}
            />
        );
    }

    return (
        <OuterStackPanel>
            <StyledContainer>
                <StyledContent>
                    {status === "success" && (
                        <>
                            <Ds.Text size="medium" as="h1" weight="bold">
                                You are now logged in
                            </Ds.Text>
                            <InnerStackPanel horizontalContentAlignment="stretch">
                                <StyledCenteredButtonLink
                                    href={data.launchUrl}
                                    text="Open Accurx desktop toolbar"
                                    onClick={() =>
                                        track("DesktopLaunch Button Click", {
                                            loginMethod,
                                        })
                                    }
                                />
                            </InnerStackPanel>
                            <Ds.Text size="small">
                                You can close this page once you're logged in on
                                the toolbar.
                            </Ds.Text>
                        </>
                    )}

                    {status === "error" && (
                        <InnerStackPanel horizontalContentAlignment="stretch">
                            <Ds.Text size="medium" weight="bold" as="h1">
                                Something went wrong
                            </Ds.Text>
                        </InnerStackPanel>
                    )}

                    {status === "loading" && <Ds.Spinner />}
                </StyledContent>
            </StyledContainer>
        </OuterStackPanel>
    );
};
